// ============================================================================
// consultantContentState.js
// Session-backed cache for consultant tab HTML content.
//
// RENDERER_VERSION: bump this whenever any *-renderer.js changes.
// - Session cache keys include the version, so stale entries are auto-discarded.
// - isHtmlCurrentVersion() lets components reject stale DB-loaded HTML too.
// ============================================================================

export const RENDERER_VERSION = 'v2026-03-29-execution-strategy';

const VERSION_META_PATTERN = /renderer-version" content="([^"]+)"/;

/** Returns true if the HTML string was produced by the current renderer. */
export const isHtmlCurrentVersion = (html) => {
  if (!html) return false;
  const match = html.match(VERSION_META_PATTERN);
  return match ? match[1] === RENDERER_VERSION : false;
};

const storageKey = (modelId, column) =>
  `consultant-content:${RENDERER_VERSION}:${modelId || 'unknown'}:${column}`;

export const getCachedConsultantContent = ({ modelId, column }) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage.getItem(storageKey(modelId, column));
  } catch (err) {
    console.warn('getCachedConsultantContent failed:', err);
    return null;
  }
};

export const setCachedConsultantContent = ({ modelId, column, html }) => {
  try {
    if (!modelId || !column || !html) return;
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(storageKey(modelId, column), html);
  } catch (err) {
    console.warn('setCachedConsultantContent failed:', err);
  }
};

export const clearCachedConsultantContent = ({ modelId, column }) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.removeItem(storageKey(modelId, column));
  } catch (err) {
    console.warn('clearCachedConsultantContent failed:', err);
  }
};

// ── JSON result cache (session only — no backend storage) ─────────────────────
// Allows QA panel to work after page reload when only HTML is loaded from DB.

const jsonStorageKey = (modelId, column) =>
  `consultant-json:${modelId || 'unknown'}:${column}`;

export const getCachedConsultantJson = ({ modelId, column }) => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    const raw = window.sessionStorage.getItem(jsonStorageKey(modelId, column));
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    return null;
  }
};

export const setCachedConsultantJson = ({ modelId, column, json }) => {
  try {
    if (!modelId || !column || !json) return;
    if (typeof window === 'undefined' || !window.sessionStorage) return;
    window.sessionStorage.setItem(jsonStorageKey(modelId, column), JSON.stringify(json));
  } catch (err) {
    console.warn('setCachedConsultantJson failed:', err);
  }
};
